import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import EventCard from "../components/EventCard";
import MapView from "../components/MapView";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, X, Map as MapIcon, List, SlidersHorizontal } from "lucide-react";
import useSEO from "../lib/useSEO";

const cats = [
  { v: "all", l: "Toutes catégories" },
  { v: "marche", l: "Marché" }, { v: "foire", l: "Foire" },
  { v: "salon", l: "Salon" }, { v: "festival", l: "Festival" },
  { v: "exposition", l: "Exposition" },
];

export default function Events() {
  const [params, setParams] = useSearchParams();
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [priceMin, setPriceMin] = useState(params.get("price_min") || "");
  const [priceMax, setPriceMax] = useState(params.get("price_max") || "");
  const [minRating, setMinRating] = useState(params.get("min_rating") || "");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const region = params.get("region") || "";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState("list"); // list | map

  useSEO({
    title: "Événements à venir",
    description:
      "Découvrez tous les salons, foires, marchés et festivals en France. Filtrez par région, date, catégorie. Réservez votre stand en quelques clics.",
  });

  const fetchEvents = async (override) => {
    setLoading(true);
    const cfg = {};
    const r = override?.region !== undefined ? override.region : region;
    if (city) cfg.city = city;
    if (category && category !== "all") cfg.category = category;
    if (q) cfg.q = q;
    if (r) cfg.region = r;
    if (dateFrom) cfg.date_from = dateFrom;
    if (dateTo) cfg.date_to = dateTo;
    if (priceMin) cfg.price_min = parseFloat(priceMin);
    if (priceMax) cfg.price_max = parseFloat(priceMax);
    if (minRating) cfg.min_rating = parseFloat(minRating);
    const { data } = await api.get("/events", { params: cfg });
    setEvents(data);
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [region]);

  const apply = () => {
    const p = {};
    if (city) p.city = city;
    if (category !== "all") p.category = category;
    if (q) p.q = q;
    if (region) p.region = region;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (priceMin) p.price_min = priceMin;
    if (priceMax) p.price_max = priceMax;
    if (minRating) p.min_rating = minRating;
    setParams(p);
    fetchEvents();
  };
  const reset = () => {
    setCity(""); setCategory("all"); setQ("");
    setDateFrom(""); setDateTo(""); setPriceMin(""); setPriceMax(""); setMinRating("");
    setParams({}); setTimeout(() => fetchEvents({ region: "" }), 50);
  };
  const clearRegion = () => {
    const p = Object.fromEntries(params);
    delete p.region;
    setParams(p);
  };

  const activeAdvancedCount = [dateFrom, dateTo, priceMin, priceMax, minRating].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-label text-muted-foreground mb-3">Recherche</p>
          <h1 className="font-heading font-light tracking-editorial text-4xl text-ink">
            {region ? region : "Trouvez votre événement"}
          </h1>
          {region && (
            <button
              data-testid="clear-region"
              onClick={clearRegion}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
            >
              <X className="h-3 w-3" /> Retirer le filtre région
            </button>
          )}
        </div>
        {/* Mobile toggle */}
        <div className="flex gap-1 lg:hidden bg-cream-100 p-1 rounded-md self-start">
          <button
            data-testid="mobile-view-list"
            onClick={() => setMobileView("list")}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${mobileView === "list" ? "bg-white shadow-sm text-ink" : "text-muted-foreground"}`}
          >
            <List className="h-4 w-4 inline mr-1" /> Liste
          </button>
          <button
            data-testid="mobile-view-map"
            onClick={() => setMobileView("map")}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${mobileView === "map" ? "bg-white shadow-sm text-ink" : "text-muted-foreground"}`}
          >
            <MapIcon className="h-4 w-4 inline mr-1" /> Carte
          </button>
        </div>
      </div>

      <div className="bg-white border border-ink/10 rounded-md p-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <Input data-testid="filter-city" placeholder="Ville" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className="md:col-span-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Input data-testid="filter-q" placeholder="Mot-clé..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button onClick={apply} data-testid="filter-apply" className="flex-1 bg-ink hover:bg-ink/85 text-cream rounded-none">
              <Search className="h-4 w-4" />
            </Button>
            <Button onClick={reset} variant="outline" data-testid="filter-reset">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="filter-toggle-advanced"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/70 hover:text-ink"
          >
            <SlidersHorizontal className="h-3 w-3" />
            Filtres avancés
            {activeAdvancedCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center bg-accent text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1">
                {activeAdvancedCount}
              </span>
            )}
          </button>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-ink/10" data-testid="advanced-filters">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Date début</label>
              <Input type="date" data-testid="filter-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Date fin</label>
              <Input type="date" data-testid="filter-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Prix min (€)</label>
              <Input type="number" data-testid="filter-price-min" placeholder="50" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Prix max (€)</label>
              <Input type="number" data-testid="filter-price-max" placeholder="500" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Note min.</label>
              <Select value={minRating} onValueChange={(v) => setMinRating(v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-min-rating"><SelectValue placeholder="Toutes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Toutes</SelectItem>
                  <SelectItem value="3">3 ★ et +</SelectItem>
                  <SelectItem value="4">4 ★ et +</SelectItem>
                  <SelectItem value="4.5">4.5 ★ et +</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : events.length === 0 ? (
        <div className="bg-cream-50 border border-ink/10 rounded-md p-12 text-center">
          <p className="text-muted-foreground">Aucun événement trouvé.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {events.length} événement{events.length > 1 ? "s" : ""}
          </p>

          {/* Desktop: split view */}
          <div className="hidden lg:grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 grid grid-cols-1 xl:grid-cols-2 gap-5 auto-rows-min">
              {events.map((e) => <EventCard key={e.id} event={e} />)}
            </div>
            <div className="lg:col-span-2 sticky top-24 self-start" style={{ height: "calc(100vh - 7rem)" }}>
              <MapView items={events} type="event" />
            </div>
          </div>

          {/* Mobile: toggle */}
          <div className="lg:hidden">
            {mobileView === "list" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((e) => <EventCard key={e.id} event={e} />)}
              </div>
            ) : (
              <div style={{ height: "calc(100vh - 22rem)", minHeight: "400px" }}>
                <MapView items={events} type="event" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
